import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import { EntityStatus, GuideSourceFormat } from "../../types";
import type { Document } from "../../types";
import { getTenantId } from "../../utils/jwtUtils";
import {
  GUIDE_FILE_ACCEPT,
  fileRelativePath,
  inferDocumentDescription,
  inferDocumentName,
  inferOperatorEntityClass,
  isJunkUploadFile,
  readGuideFile,
} from "../../utils/guideFileReader";

type UploadMode = "file" | "folder";
type RowStatus = "pending" | "converting" | "saving" | "done" | "error";

interface UploadRow {
  key: string;
  fileName: string;
  status: RowStatus;
  message?: string;
  document?: Document;
}

interface DocumentFileUploadDialogProps {
  open: boolean;
  mode: UploadMode;
  onClose: () => void;
  onComplete: (created: Document[]) => void;
  productUuids?: string[];
  apiUuids?: string[];
}

function applyDirectoryPicker(input: HTMLInputElement | null, enabled: boolean) {
  if (!input) return;
  if (enabled) {
    input.setAttribute("webkitdirectory", "");
    input.setAttribute("directory", "");
    input.multiple = true;
  } else {
    input.removeAttribute("webkitdirectory");
    input.removeAttribute("directory");
    input.multiple = false;
  }
}

const DocumentFileUploadDialog: React.FC<DocumentFileUploadDialogProps> = ({
  open,
  mode,
  onClose,
  onComplete,
  productUuids,
  apiUuids,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRows([]);
    setBusy(false);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [open, mode]);

  const reset = () => {
    setRows([]);
    setBusy(false);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    if (busy) return;
    const created = rows
      .map((row) => row.document)
      .filter((doc): doc is Document => Boolean(doc));
    reset();
    onClose();
    if (created.length > 0) {
      onComplete(created);
    }
  };

  const updateRow = (key: string, patch: Partial<UploadRow>) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const processFiles = async (fileList: FileList | null) => {
    const tenantId = getTenantId();
    if (!tenantId) {
      setError("Tenant context is required");
      return;
    }
    const files = Array.from(fileList ?? []).filter((file) => !isJunkUploadFile(file));
    if (files.length === 0) {
      setError(mode === "folder" ? "No importable files found in that folder." : "No file selected.");
      return;
    }

    const initial: UploadRow[] = files.map((file, index) => ({
      key: `${fileRelativePath(file)}:${index}`,
      fileName: fileRelativePath(file),
      status: "pending",
    }));
    setRows(initial);
    setBusy(true);
    setError(null);

    const created: Document[] = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const key = initial[i].key;
      try {
        updateRow(key, { status: "converting" });
        const result = await readGuideFile(file);
        const name = inferDocumentName(file.name, result.text, result.title);
        const description = inferDocumentDescription(result.text, file.name);
        const entityClass = inferOperatorEntityClass(file.name, fileRelativePath(file));
        updateRow(key, { status: "saving" });
        const document = await apiService.createDocument({
          tenantId,
          name,
          description,
          status: EntityStatus.DRAFT,
          entityClass,
          content: result.text,
          sourceFormat: result.sourceFormat ?? GuideSourceFormat.MARKDOWN,
          sourceFileName: file.name,
          productUuids,
          apiUuids,
        });
        created.push(document);
        updateRow(key, { status: "done", document, message: name });
      } catch (err: unknown) {
        updateRow(key, {
          status: "error",
          message: err instanceof Error ? err.message : "Failed to import file",
        });
      }
    }
    setBusy(false);
    if (created.length === 0) {
      setError("No documents were saved.");
    }
  };

  const title = mode === "folder" ? "Upload from folder" : "Upload file";
  const helper =
    mode === "folder"
      ? "Choose a folder. Each file is converted to Markdown if needed, then saved as a draft. Name and description are inferred from the file."
      : "Choose a single file. Non-text files are converted to Markdown via the CMS converter, then saved as a draft.";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helper}
        </Typography>
        <Button
          component="label"
          variant="outlined"
          disabled={busy}
          sx={{ textTransform: "none" }}
        >
          {mode === "folder" ? "Choose folder" : "Choose file"}
          <input
            ref={(el) => {
              inputRef.current = el;
              applyDirectoryPicker(el, mode === "folder");
            }}
            type="file"
            hidden
            accept={mode === "file" ? GUIDE_FILE_ACCEPT : undefined}
            onChange={(e) => {
              void processFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </Button>
        {busy && <LinearProgress sx={{ mt: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {rows.length > 0 && (
          <List dense sx={{ mt: 1, maxHeight: 320, overflow: "auto" }}>
            {rows.map((row) => (
              <ListItem key={row.key} disableGutters>
                <ListItemText
                  primary={row.fileName}
                  secondary={
                    row.status === "done"
                      ? `Saved as draft: ${row.message}`
                      : row.status === "error"
                        ? row.message
                        : row.status === "converting"
                          ? "Converting to Markdown…"
                          : row.status === "saving"
                            ? "Saving…"
                            : "Waiting"
                  }
                  secondaryTypographyProps={{
                    color: row.status === "error" ? "error" : "text.secondary",
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
        {!busy && rows.some((row) => row.status === "done") && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Drafts are ready. Close this dialog to view or edit them.
          </Alert>
        )}
        <Box sx={{ mt: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Status is always DRAFT. Binary files (PDF, Word, PowerPoint, images) go through
              POST /markdown/convert first.
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          {rows.some((row) => row.status === "done") ? "Done" : "Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentFileUploadDialog;
